// Kept identical to bean_admin/src/waitlistReason.ts; never render arbitrary diagnostic payloads.
type RecordValue = Record<string, unknown>;
const obj = (v: unknown): RecordValue => v && typeof v === "object" && !Array.isArray(v) ? v as RecordValue : {};
const code = (v: unknown): string => typeof v === "string" && /^[a-z0-9_-]{1,64}$/.test(v) ? v : "";
const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
export interface ReasonDescription { summary: string; detail: string; evidence: string; next: string }
const server: Record<string, [string, string, string]> = {
  missing_token: ["未取得验证令牌", "提交时没有带上浏览器验证令牌，后端未向 Cloudflare 发起校验。", "结合下方前端阶段排查；缺少历史记录时需重新验证，不能判断为机器人。"],
  "invalid-input-response": ["验证令牌无效", "Cloudflare 拒绝了提交的令牌，可能无效、格式异常或过期；没有返回更细原因，不能据此判断为机器人。", "重新获取新令牌后再验证；反复失败转人工排查。"],
  "timeout-or-duplicate": ["验证令牌过期或已使用", "Cloudflare 返回令牌超时或重复使用。", "获取新令牌后重新验证，不要反复提交同一个令牌。"],
  timeout: ["后端校验等待超时", "服务器等待 Cloudflare 响应超过了时限，不代表用户验证被判失败。", "检查校验耗时及重试结果；仍失败转人工处理。"],
  provider_error: ["后端校验请求异常", "校验请求未正常完成；可能是网络或服务异常，现有记录不能确定更细原因。", "检查服务日志和重试结果。"],
  "internal-error": ["Cloudflare 内部错误", "Cloudflare 返回内部服务错误。", "稍后重试；持续发生时检查服务状态。"],
  missing_secret: ["验证密钥未配置", "服务器缺少验证密钥。", "由开发人员检查部署配置，用户重复提交通常无法解决。"],
  "missing-input-secret": ["校验请求缺少密钥", "Cloudflare 未收到验证密钥。", "由开发人员检查请求和密钥配置。"],
  "invalid-input-secret": ["验证密钥无效", "Cloudflare 不认可服务器的验证密钥。", "由开发人员检查密钥配置。"],
  action_mismatch: ["验证场景不匹配", "令牌的 action 与当前表单预期不一致。", "由开发人员检查前后端 action 配置。"],
  hostname_mismatch: ["验证域名不匹配", "令牌所属域名与服务端允许域名不一致。", "由开发人员检查域名配置。"],
  invalid_response: ["校验服务响应无法解析", "后端未能解析校验服务响应。", "检查响应格式和服务日志。"],
  "bad-request": ["校验请求格式异常", "Cloudflare 无法处理请求格式。", "由开发人员检查校验请求。"],
  "missing-input-response": ["Cloudflare 未收到令牌", "浏览器到服务器、服务器到 Cloudflare 的令牌传递需要核查。", "检查请求字段并重新获取令牌。"],
  challenge_failed: ["验证未通过，细因未知", "服务未返回可识别的具体错误码。", "结合前端记录排查，不推断为机器人。"],
};
const client: Record<string, string> = {
  script_load_failed: "验证脚本加载失败", script_timeout: "验证脚本加载超时",
  script_unavailable: "验证脚本接口不可用", widget_initialization_failed: "验证组件初始化失败",
  client_wait_timeout: "浏览器等待令牌超时", provider_timeout: "验证组件报告挑战超时",
  provider_error: "验证组件返回错误", token_expired: "浏览器报告令牌过期",
  unsupported_browser: "浏览器不受验证组件支持", missing_sitekey: "页面缺少验证站点密钥",
  missing_slot: "页面缺少验证组件容器", widget_remove_failed: "重建验证组件失败",
};
const other: Record<string, [string, string, string]> = {
  android: ["设备暂不支持", "申请选择了 Android；当前入口仅支持 iMessage，未执行后续验证。", "核对设备选择；Android 用户保留等待名单。"],
  invalid_phone: ["手机号校验未通过", "号码无法按当前规则识别为有效号码，不代表 Turnstile 失败。", "核对完整号码及国家区号，不猜测或擅自改号。"],
  content_junk: ["填写内容被自动审核判为无效", "自动内容审核认为信息缺乏有效含义或疑似测试；这是模型判断，不是已确认的垃圾用户。", "人工查看原始填写，必要时请用户补充具体需求。"],
  content_uncertain: ["内容审核无法确定", "自动审核未能得出确定结论。", "人工查看填写内容和审核错误码。"],
  capacity_full: ["当前名额已满", "申请未获得自动预留名额。", "等待名额释放或人工安排。"],
  capacity_uninitialized: ["名额计数尚未初始化", "系统名额配置未准备好。", "由管理员检查名额配置。"],
  capacity_invalid: ["名额配置异常", "系统无法使用当前名额配置。", "由管理员修复配置。"],
  reservation_error: ["预留名额失败", "系统未能完成名额预留。", "检查服务日志和实际预约状态，避免重复发放。"],
  auto_access_disabled: ["自动审批未开启", "本次未执行自动批准。", "由管理员人工处理。"],
  reserved: ["审核通过，已预留名额", "", ""], already_reserved: ["该号码已有有效名额", "", ""], already_active: ["该号码已有使用权限", "", ""],
};
export function describeWaitlistReason(raw: unknown): ReasonDescription {
  const r = obj(raw), e = obj(r.accessEvaluation), t = obj(e.turnstile);
  const reason = code(e.reasonCode), turnstileCode = code(t.reasonCode);
  const entry = reason === "turnstile_failed" ? server[turnstileCode] : other[reason];
  let [summary, detail, next] = entry || [reason === "turnstile_failed" ? "验证未通过，具体原因未识别" : "审批原因尚未识别", "现有记录不足以确定具体原因。", "查看原始错误码并由管理员排查。"];
  if (reason === "turnstile_failed" && /^http_\d{3}$/.test(turnstileCode)) {
    summary = "校验服务返回 HTTP 错误"; detail = "校验接口未正常响应，不是用户身份判定。"; next = "检查 HTTP 状态码与服务日志。";
  }
  const evidence: string[] = [];
  if (reason) evidence.push(`审批代码：${reason}`);
  if (reason === "turnstile_failed") {
    evidence.push(`后端代码：${turnstileCode || "未记录"}`);
    if (finite(t.latencyMs)) evidence.push(`后端耗时：${t.latencyMs}ms`);
    evidence.push(`后端尝试：${Array.isArray(t.attempts) ? t.attempts.length + " 次" : "未记录"}`);
    const d = obj(r.clientVerification);
    const events = d.version === "client_recovery_v1" && Array.isArray(d.events) ? d.events.slice(-40).map(obj) : [];
    // Only report the final observed failure; do not present an earlier recovered error as the cause.
    const lastToken = events.map(x => x.phase).lastIndexOf("token_ready");
    const failure = events.slice(lastToken + 1).reverse().find(x => client[code(x.reason)]);
    if (failure) {
      evidence.push(`前端观察：${client[code(failure.reason)]}`);
      if (typeof failure.errorCode === "string" && /^\d{1,12}$/.test(failure.errorCode)) evidence.push(`组件错误码：${failure.errorCode}`);
    } else evidence.push(events.length ? "前端未记录未恢复的错误" : "前端具体原因未记录（历史记录或诊断缺失）");
    if (events.length) evidence.push(`前端重试：${events.filter(x => x.phase === "retry").length} 次`);
  } else if (reason === "content_junk" || reason === "content_uncertain") {
    const c = obj(e.classifier); if (code(c.reasonCode)) evidence.push(`内容审核代码：${code(c.reasonCode)}`);
  }
  return { summary, detail, next, evidence: evidence.join("；") };
}
